(function () {
  "use strict";

  /* =========================================================
     BASIC HELPERS
  ========================================================= */

  function escapeHtml(value = "") {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function escapeRegExp(value = "") {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  function normalizeKeywords(value) {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .map((item) => String(item).trim())
        .filter(Boolean);
    }

    return String(value)
      .split(/[,，;；\n]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function normalizeFaq(rawFaq, index) {
  return {
    id: rawFaq.id || `faq-${index + 1}`,
    primary: String(rawFaq.primary || "").trim(),
    secondary: String(rawFaq.secondary || "").trim(),
    question: String(rawFaq.question || "").trim(),
    answer: String(rawFaq.answer || "").trim(),

    steps: Array.isArray(rawFaq.steps)
      ? rawFaq.steps
          .map((step) => String(step || "").trim())
          .filter(Boolean)
      : [],

    subitemsAfterStep: Number(
      rawFaq.subitemsAfterStep || 0
    ),

    subitems: Array.isArray(rawFaq.subitems)
      ? rawFaq.subitems
          .map((item) => String(item || "").trim())
          .filter(Boolean)
      : [],

    images: Array.isArray(rawFaq.images)
      ? rawFaq.images.filter(Boolean)
      : [],

    keywords: normalizeKeywords(rawFaq.keywords)
  };
}

  /* =========================================================
     SHOPIFY HEADER
     只检测 Header，不再扫描 body *
  ========================================================= */

  function getVisibleTopOffset() {
    let topOffset = 0;

    const selectors = [
      "header",
      "[role='banner']",
      ".header-wrapper",
      ".shopify-section-header-sticky",
      ".shopify-section-group-header-group",
      ".announcement-bar-section",
      ".shopify-section[id*='announcement']"
    ];

    document
      .querySelectorAll(selectors.join(","))
      .forEach((element) => {
        if (
          element.closest &&
          element.closest(".enecess-faq")
        ) {
          return;
        }

        const rect = element.getBoundingClientRect();

        if (
          rect.width <= 0 ||
          rect.height <= 0 ||
          rect.bottom <= 0 ||
          rect.top > 5
        ) {
          return;
        }

        if (
          rect.height >
          window.innerHeight * 0.45
        ) {
          return;
        }

        const style = window.getComputedStyle(element);

        if (
          style.position !== "fixed" &&
          style.position !== "sticky" &&
          rect.top > 1
        ) {
          return;
        }

        topOffset = Math.max(
          topOffset,
          rect.bottom
        );
      });

    return Math.max(
      0,
      Math.ceil(topOffset)
    );
  }

  function updateFaqStickyOffset() {
    const roots =
      document.querySelectorAll(".enecess-faq");

    if (!roots.length) return;

    const offset =
      getVisibleTopOffset();

    if (offset > 0) {
      roots.forEach((root) => {
        root.style.setProperty(
          "--faq-header-offset",
          `${offset}px`
        );
      });
    }
  }

  let headerFrame = null;

  function scheduleHeaderUpdate() {
    if (headerFrame) {
      cancelAnimationFrame(headerFrame);
    }

    headerFrame =
      requestAnimationFrame(() => {
        updateFaqStickyOffset();
        headerFrame = null;
      });
  }

  /* =========================================================
     LOCALE
  ========================================================= */

  function languageCodeFromLocale(locale) {
    const value = String(locale || "en")
      .replace("_", "-")
      .toLowerCase();

    if (value === "pt-br") return "PT_BR";
    if (value === "pt-pt") return "PT_PT";
    if (value === "zh-cn") return "ZH_CN";
    if (value === "zh-tw") return "ZH_TW";

    return value
      .split("-")[0]
      .toUpperCase();
  }

  /* =========================================================
     STOREFRONT API HELPERS
  ========================================================= */

  function fieldMap(node) {
    const map = new Map();

    (node.fields || []).forEach((field) => {
      map.set(field.key, field);
    });

    return map;
  }

  function fieldValue(fields, key) {
    return fields.get(key)?.value || "";
  }

  function fieldImageUrl(fields, key) {
    const reference =
      fields.get(key)?.reference;

    if (!reference) return "";

    if (
      reference.__typename ===
      "MediaImage"
    ) {
      return reference.image?.url || "";
    }

    if (
      reference.__typename ===
      "GenericFile"
    ) {
      return reference.url || "";
    }

    return "";
  }

  function storefrontNodeToFaq(node) {
    const fields = fieldMap(node);

    return {
      id: node.handle || node.id,

      primary: fieldValue(
        fields,
        "primary_category"
      ),

      secondary: fieldValue(
        fields,
        "secondary_category"
      ),

      question: fieldValue(
        fields,
        "question"
      ),

      answer: fieldValue(
        fields,
        "answer"
      ),

      keywords: fieldValue(
        fields,
        "keywords"
      ),

      steps: [
  fieldValue(fields, "step_1"),
  fieldValue(fields, "step_2"),
  fieldValue(fields, "step_3"),
  fieldValue(fields, "step_4"),
  fieldValue(fields, "step_5"),
  fieldValue(fields, "step_6")
].filter(Boolean),

subitemsAfterStep: Number(
  fieldValue(fields, "subitems_after_step") || 0
),

subitems: [
  fieldValue(fields, "step_subitem_1"),
  fieldValue(fields, "step_subitem_2"),
  fieldValue(fields, "step_subitem_3")
].filter(Boolean),

images: [
  fieldImageUrl(fields, "image_1"),
  fieldImageUrl(fields, "image_2"),
  fieldImageUrl(fields, "image_3"),
  fieldImageUrl(fields, "image_4"),
  fieldImageUrl(fields, "image_5")
].filter(Boolean)
    };
  }

  /* =========================================================
     STOREFRONT API
     只有 FAQ >= 250 时才需要
  ========================================================= */

  async function fetchAllStorefrontFaqs(root) {
    const domain =
      root.dataset.storefrontDomain;

    const token =
      root.dataset.storefrontToken;

    const apiVersion =
      root.dataset.storefrontApiVersion ||
      "2026-07";

    const locale =
      root.dataset.locale ||
      "en";

    if (!domain || !token) {
      return null;
    }

    const endpoint =
      `https://${domain}/api/${apiVersion}/graphql.json`;

    const language =
      languageCodeFromLocale(locale);

    const query = `
      query HelpCenterFaqs(
        $after: String,
        $language: LanguageCode!
      )
      @inContext(language: $language) {

        metaobjects(
          type: "faq"
          first: 250
          after: $after
          sortKey: "id"
        ) {

          nodes {
            id
            handle

            fields {
              key
              value

              reference {
                __typename

                ... on MediaImage {
                  image {
                    url
                  }
                }

                ... on GenericFile {
                  url
                }
              }
            }
          }

          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    const allNodes = [];

    let after = null;
    let safetyCounter = 0;

    while (true) {
      safetyCounter += 1;

      if (safetyCounter > 40) {
        throw new Error(
          "FAQ pagination stopped."
        );
      }

      const response =
        await fetch(endpoint, {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            "X-Shopify-Storefront-Access-Token":
              token
          },

          body: JSON.stringify({
            query,

            variables: {
              after,
              language
            }
          })
        });

      if (!response.ok) {
        throw new Error(
          `FAQ API error ${response.status}`
        );
      }

      const payload =
        await response.json();

      if (payload.errors?.length) {
        throw new Error(
          payload.errors
            .map((error) => error.message)
            .join(" | ")
        );
      }

      const connection =
        payload.data?.metaobjects;

      if (!connection) {
        throw new Error(
          "FAQ API returned no data."
        );
      }

      allNodes.push(
        ...(connection.nodes || [])
      );

      if (
        !connection.pageInfo?.hasNextPage
      ) {
        break;
      }

      after =
        connection.pageInfo.endCursor;

      if (!after) break;
    }

    return allNodes.map(
      storefrontNodeToFaq
    );
  }

  /* =========================================================
     LIQUID DATA
  ========================================================= */

  function readLiquidFallback(root) {
    const node =
      root.querySelector(
        "[data-faq-data]"
      );

    if (!node) return [];

    try {
      return JSON.parse(
        node.textContent.trim() || "[]"
      );
    } catch (error) {
      console.error(
        "FAQ Liquid JSON error:",
        error
      );

      return [];
    }
  }

  /* =========================================================
     FAQ APP
  ========================================================= */

  function initFaq(root) {
    if (
      !root ||
      root.dataset.initialized === "true"
    ) {
      return;
    }

    root.dataset.initialized = "true";

    const categoryMap =
      root.querySelector(
        "[data-faq-category-map]"
      );

    const faqList =
      root.querySelector(
        "[data-faq-list]"
      );

    const faqResults =
      root.querySelector(
        "[data-faq-results]"
      );

    const emptyState =
      root.querySelector(
        "[data-faq-empty]"
      );

    const searchInput =
      root.querySelector(
        "[data-faq-search]"
      );

    const clearButton =
      root.querySelector(
        "[data-faq-clear]"
      );

    const allButton =
      root.querySelector(
        "[data-faq-all]"
      );

    const activeTitle =
      root.querySelector(
        "[data-faq-active-title]"
      );

    if (
      !categoryMap ||
      !faqResults ||
      !searchInput ||
      !clearButton ||
      !allButton ||
      !activeTitle
    ) {
      return;
    }

    const PAGE_SIZE = 20;

    let faqs = [];

    const ui = {
      allFaqs:
        root.dataset.allFaqs ||
        "All FAQs",

      faq:
        root.dataset.faqSingular ||
        "FAQ",

      screenshot:
        root.dataset.screenshot ||
        "Screenshot",

      loading:
        root.dataset.loading ||
        "Loading FAQs...",

      error:
        root.dataset.loadError ||
        "FAQ data could not be loaded.",

      errorHelp:
        root.dataset.loadErrorHelp ||
        "Please refresh the page."
    };

    const state = {
      primary: "All",
      secondary: "All",
      query: "",
      currentPage: 1,
      openQuestions: new Set(),
      manualOpen: false
    };

    /* =========================================================
       PAGINATION
    ========================================================= */

    let pagination =
      root.querySelector(
        "[data-faq-pagination]"
      );

    if (!pagination) {
      pagination =
        document.createElement("nav");

      pagination.className =
        "faq-pagination";

      pagination.setAttribute(
        "data-faq-pagination",
        ""
      );

      pagination.setAttribute(
        "aria-label",
        "FAQ pagination"
      );

      faqResults.insertAdjacentElement(
        "afterend",
        pagination
      );
    }

    /* =========================================================
       CATEGORIES
    ========================================================= */

    function buildCategories() {
      const map = new Map();

      faqs.forEach((faq) => {
        if (!faq.primary) return;

        if (!map.has(faq.primary)) {
          map.set(
            faq.primary,
            new Set()
          );
        }

        if (faq.secondary) {
          map
            .get(faq.primary)
            .add(faq.secondary);
        }
      });

      return Array.from(map.entries())
        .map(([name, children]) => ({
          name,
          children:
            Array.from(children)
        }));
    }

    function primaryCount(name) {
      return faqs.filter(
        (faq) =>
          faq.primary === name
      ).length;
    }

    function secondaryCount(
      primary,
      secondary
    ) {
      return faqs.filter(
        (faq) =>
          faq.primary === primary &&
          faq.secondary === secondary
      ).length;
    }

    function renderCategories() {
      const categories =
        buildCategories();

      allButton.textContent =
        `${ui.allFaqs} (${faqs.length})`;

      allButton.classList.toggle(
        "active",
        state.primary === "All" &&
        state.secondary === "All"
      );

      categoryMap.innerHTML =
        categories
          .map((category) => {
            const active =
              state.primary ===
              category.name;

            const children =
              category.children
                .map((child) => {
                  const childActive =
                    active &&
                    state.secondary === child;

                  return `
                    <button
                      type="button"
                      class="map-chip ${
                        childActive
                          ? "active"
                          : ""
                      }"
                      data-faq-primary="${escapeHtml(
                        category.name
                      )}"
                      data-faq-secondary="${escapeHtml(
                        child
                      )}"
                    >
                      <span>
                        ${escapeHtml(child)}
                      </span>

                      <small>
                        ${secondaryCount(
                          category.name,
                          child
                        )}
                      </small>
                    </button>
                  `;
                })
                .join("");

            return `
              <article
                class="map-group ${
                  active
                    ? "active"
                    : ""
                }"
              >

                <button
                  type="button"
                  class="map-title"
                  data-faq-primary="${escapeHtml(
                    category.name
                  )}"
                  aria-expanded="${
                    active
                      ? "true"
                      : "false"
                  }"
                >
                  <span>
                    ${escapeHtml(
                      category.name
                    )}
                  </span>

                  <span class="map-meta">

                    <span class="map-count">
                      ${primaryCount(
                        category.name
                      )}
                      ${escapeHtml(ui.faq)}
                    </span>

                    <span
                      class="map-arrow"
                      aria-hidden="true"
                    >
                      ⌄
                    </span>

                  </span>
                </button>

                <div class="map-items">
                  ${children}
                </div>

              </article>
            `;
          })
          .join("");
    }

    /* =========================================================
       SEARCH
    ========================================================= */

    function highlight(text) {
      const safeText =
        escapeHtml(text);

      const query =
        state.query.trim();

      if (!query) {
        return safeText;
      }

      try {
        const regex =
          new RegExp(
            `(${escapeRegExp(
              escapeHtml(query)
            )})`,
            "ig"
          );

        return safeText.replace(
          regex,
          "<mark>$1</mark>"
        );
      } catch {
        return safeText;
      }
    }

    function matchesFaq(faq) {
      if (
        state.primary !== "All" &&
        faq.primary !== state.primary
      ) {
        return false;
      }

      if (
        state.secondary !== "All" &&
        faq.secondary !==
          state.secondary
      ) {
        return false;
      }

      const query =
        state.query
          .trim()
          .toLowerCase();

      if (!query) return true;

      const searchable = [
        faq.question,
        faq.answer,
        faq.primary,
        faq.secondary,
        ...faq.steps,
        ...faq.subitems,
        ...faq.keywords
      ]
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    }

    function getResults() {
      return faqs.filter(matchesFaq);
    }

    /* =========================================================
       FAQ HTML
    ========================================================= */

    function renderSteps(faq) {
  if (!faq.steps.length) {
    return "";
  }

  const subitemsAfterStep =
    Number(faq.subitemsAfterStep || 0);

  const hasSubitems =
    Array.isArray(faq.subitems) &&
    faq.subitems.length > 0;

  return `
    <ol class="faq-steps">
      ${faq.steps
        .map((step, index) => {
          const stepNumber = index + 1;

          const subitemsHtml =
            hasSubitems &&
            subitemsAfterStep === stepNumber
              ? `
                <ul class="faq-step-subitems">
                  ${faq.subitems
                    .map(
                      (item) => `
                        <li>
                          ${highlight(item)}
                        </li>
                      `
                    )
                    .join("")}
                </ul>
              `
              : "";

          return `
            <li>
              <div class="faq-step-text">
                ${highlight(step)}
              </div>

              ${subitemsHtml}
            </li>
          `;
        })
        .join("")}
    </ol>
  `;
}

    function renderImages(faq) {
      if (!faq.images.length) {
        return "";
      }

      return `
        <div class="faq-images">

          ${faq.images
            .map(
              (image, index) => `
                <figure>
                  <img
                    src="${escapeHtml(
                      image
                    )}"
                    alt="${escapeHtml(
                      `${faq.question} ${ui.screenshot} ${index + 1}`
                    )}"
                    loading="lazy"
                    decoding="async"
                  >
                </figure>
              `
            )
            .join("")}

        </div>
      `;
    }

    /* =========================================================
       PAGINATION
    ========================================================= */

    function totalPages(total) {
      return Math.max(
        1,
        Math.ceil(
          total / PAGE_SIZE
        )
      );
    }

    function pageNumbers(
      current,
      total
    ) {
      if (total <= 7) {
        return Array.from(
          { length: total },
          (_, index) =>
            index + 1
        );
      }

      if (current <= 4) {
        return [
          1,
          2,
          3,
          4,
          5,
          "...",
          total
        ];
      }

      if (
        current >=
        total - 3
      ) {
        return [
          1,
          "...",
          total - 4,
          total - 3,
          total - 2,
          total - 1,
          total
        ];
      }

      return [
        1,
        "...",
        current - 1,
        current,
        current + 1,
        "...",
        total
      ];
    }

    function renderPagination(total) {
      const pages =
        totalPages(total);

      state.currentPage =
        Math.min(
          Math.max(
            state.currentPage,
            1
          ),
          pages
        );

      if (total <= PAGE_SIZE) {
        pagination.hidden = true;
        pagination.innerHTML = "";
        return;
      }

      pagination.hidden = false;

      const buttons =
        pageNumbers(
          state.currentPage,
          pages
        )
          .map((page) => {
            if (page === "...") {
              return `
                <span
                  class="pagination-ellipsis"
                >
                  …
                </span>
              `;
            }

            return `
              <button
                type="button"
                class="pagination-page ${
                  page ===
                  state.currentPage
                    ? "active"
                    : ""
                }"
                data-faq-page="${page}"
                ${
                  page ===
                  state.currentPage
                    ? 'aria-current="page"'
                    : ""
                }
              >
                ${page}
              </button>
            `;
          })
          .join("");

      pagination.innerHTML = `
        <button
          type="button"
          class="pagination-arrow"
          data-faq-page-action="previous"
          ${
            state.currentPage === 1
              ? "disabled"
              : ""
          }
        >
          Previous
        </button>

        <div class="pagination-pages">
          ${buttons}
        </div>

        <button
          type="button"
          class="pagination-arrow"
          data-faq-page-action="next"
          ${
            state.currentPage === pages
              ? "disabled"
              : ""
          }
        >
          Next
        </button>
      `;
    }

    /* =========================================================
       ACTIVE TITLE
    ========================================================= */

    function getActiveTitle() {
      if (state.primary === "All") {
        return ui.allFaqs;
      }

      if (state.secondary === "All") {
        return state.primary;
      }

      return (
        `${state.primary} / ` +
        state.secondary
      );
    }

    /* =========================================================
       RENDER FAQ
    ========================================================= */

    function renderFaqs() {
      const results =
        getResults();

      const pages =
        totalPages(
          results.length
        );

      if (
        state.currentPage > pages
      ) {
        state.currentPage = 1;
      }

      const start =
        (state.currentPage - 1) *
        PAGE_SIZE;

      const pageResults =
        results.slice(
          start,
          start + PAGE_SIZE
        );

      activeTitle.textContent =
        getActiveTitle();

      if (emptyState) {
        emptyState.hidden =
          results.length > 0;
      }

      faqResults.innerHTML =
        pageResults
          .map((faq, index) => {
            let open = false;

            if (state.manualOpen) {
              open =
                state.openQuestions
                  .has(faq.id);
            } else {
              open = index === 0;
            }

            return `
              <details
                class="faq-card"
                data-faq-id="${escapeHtml(
                  faq.id
                )}"
                ${
                  open
                    ? "open"
                    : ""
                }
              >

                <summary>

                  <div class="card-topline">

                    ${
                      faq.primary
                        ? `
                          <span class="tag">
                            ${escapeHtml(
                              faq.primary
                            )}
                          </span>
                        `
                        : ""
                    }

                    ${
                      faq.secondary
                        ? `
                          <span
                            class="tag secondary"
                          >
                            ${escapeHtml(
                              faq.secondary
                            )}
                          </span>
                        `
                        : ""
                    }

                  </div>

                  <div class="question-row">

                    <h3>
                      ${highlight(
                        faq.question
                      )}
                    </h3>

                    <span
                      class="chevron"
                      aria-hidden="true"
                    >
                      +
                    </span>

                  </div>

                </summary>

                <div class="answer">

                  ${
                    faq.answer
                      ? `
                        <p>
                          ${highlight(
                            faq.answer
                          )}
                        </p>
                      `
                      : ""
                  }

                  ${renderSteps(faq)}

                  ${renderImages(faq)}

                </div>

              </details>
            `;
          })
          .join("");

      renderPagination(
        results.length
      );
    }

    function render() {
      renderCategories();
      renderFaqs();
    }

    /* =========================================================
       RESET
    ========================================================= */

    function resetPage() {
      state.currentPage = 1;
    }

    function resetOpen() {
      state.openQuestions.clear();
      state.manualOpen = false;
    }

    function scrollToResults() {
      if (!faqList) return;

      requestAnimationFrame(() => {
        faqList.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      });
    }

    /* =========================================================
       CATEGORY EVENTS
    ========================================================= */

    categoryMap.addEventListener(
      "click",
      (event) => {

        const secondary =
          event.target.closest(
            "[data-faq-secondary]"
          );

        if (secondary) {
          state.primary =
            secondary.dataset
              .faqPrimary;

          state.secondary =
            secondary.dataset
              .faqSecondary;

          resetPage();
          resetOpen();

          render();
          scrollToResults();

          return;
        }

        const primary =
          event.target.closest(
            "[data-faq-primary]"
          );

        if (!primary) return;

        const name =
          primary.dataset
            .faqPrimary;

        if (
          state.primary === name &&
          state.secondary === "All"
        ) {
          state.primary = "All";
        } else {
          state.primary = name;
        }

        state.secondary = "All";

        resetPage();
        resetOpen();

        render();
        scrollToResults();
      }
    );

    /* =========================================================
       ALL
    ========================================================= */

    allButton.addEventListener(
      "click",
      () => {
        state.primary = "All";
        state.secondary = "All";

        resetPage();
        resetOpen();

        render();
        scrollToResults();
      }
    );

    /* =========================================================
       SEARCH
    ========================================================= */

    let searchTimer = null;

    searchInput.addEventListener(
      "input",
      (event) => {

        clearTimeout(searchTimer);

        const value =
          event.target.value;

        /*
          轻微 debounce，
          避免用户每输入一个字母都立即重绘。
        */
        searchTimer =
          setTimeout(() => {
            state.query = value;

            resetPage();
            resetOpen();

            renderFaqs();
          }, 80);
      }
    );

    /* =========================================================
       CLEAR
    ========================================================= */

    clearButton.addEventListener(
      "click",
      () => {

        clearTimeout(searchTimer);

        state.primary = "All";
        state.secondary = "All";
        state.query = "";

        searchInput.value = "";

        resetPage();
        resetOpen();

        render();
        scrollToResults();

        searchInput.focus();
      }
    );

    /* =========================================================
       PAGINATION EVENTS
    ========================================================= */

    pagination.addEventListener(
      "click",
      (event) => {

        const pageButton =
          event.target.closest(
            "[data-faq-page]"
          );

        if (pageButton) {
          state.currentPage =
            Number(
              pageButton.dataset
                .faqPage
            ) || 1;

          resetOpen();

          renderFaqs();
          scrollToResults();

          return;
        }

        const action =
          event.target.closest(
            "[data-faq-page-action]"
          );

        if (
          !action ||
          action.disabled
        ) {
          return;
        }

        const pages =
          totalPages(
            getResults().length
          );

        if (
          action.dataset
            .faqPageAction ===
          "previous"
        ) {
          state.currentPage =
            Math.max(
              1,
              state.currentPage - 1
            );
        }

        if (
          action.dataset
            .faqPageAction ===
          "next"
        ) {
          state.currentPage =
            Math.min(
              pages,
              state.currentPage + 1
            );
        }

        resetOpen();

        renderFaqs();
        scrollToResults();
      }
    );

    /* =========================================================
       ACCORDION
    ========================================================= */

    faqResults.addEventListener(
      "toggle",
      (event) => {

        const details =
          event.target;

        if (
          !(details instanceof HTMLDetailsElement) ||
          !details.classList.contains(
            "faq-card"
          )
        ) {
          return;
        }

        const id =
          details.dataset.faqId;

        if (!id) return;

        state.manualOpen = true;

        if (details.open) {
          state.openQuestions.add(id);
        } else {
          state.openQuestions.delete(id);
        }
      },
      true
    );

    /* =========================================================
       LOADING
  ========================================================= */

    function showLoading() {
      allButton.textContent =
        ui.loading;

      activeTitle.textContent =
        ui.loading;
    }

    function showError(error) {
      console.error(
        "enecess FAQ:",
        error
      );

      allButton.textContent =
        ui.allFaqs;

      activeTitle.textContent =
        ui.error;

      faqResults.innerHTML = `
        <div class="empty-state">
          <h3>
            ${escapeHtml(ui.error)}
          </h3>

          <p>
            ${escapeHtml(
              ui.errorHelp
            )}
          </p>
        </div>
      `;
    }

    /* =========================================================
       FAST DATA LOAD

       关键优化：
       先显示 Liquid 数据。
       少于250条不再请求 API。
  ========================================================= */

    async function load() {
      const liquidFaqs =
        readLiquidFallback(root)
          .map(normalizeFaq)
          .filter(
            (faq) =>
              faq.question &&
              faq.primary
          );

      /*
        第一时间显示 Shopify Liquid
        已经输出的数据。
      */
      if (liquidFaqs.length) {
        faqs = liquidFaqs;

        resetPage();

        render();
      }

      /*
        当前不足250条：
        说明 Liquid 已经读取完整，
        不需要重复 API 请求。
      */
      if (
        liquidFaqs.length > 0 &&
        liquidFaqs.length < 250
      ) {
        return;
      }

      const hasApi =
        Boolean(
          root.dataset
            .storefrontDomain &&
          root.dataset
            .storefrontToken
        );

      /*
        没 API 配置。
      */
      if (!hasApi) {
        if (!liquidFaqs.length) {
          showError(
            new Error(
              "No FAQ data available."
            )
          );
        }

        return;
      }

      /*
        如果 Liquid 完全没数据，
        才显示 Loading。
      */
      if (!liquidFaqs.length) {
        showLoading();
      }

      try {
        const apiData =
          await fetchAllStorefrontFaqs(
            root
          );

        const apiFaqs =
          (apiData || [])
            .map(normalizeFaq)
            .filter(
              (faq) =>
                faq.question &&
                faq.primary
            );

        /*
          API 数据更多才替换。
      */
        if (
          apiFaqs.length >
            liquidFaqs.length ||
          !liquidFaqs.length
        ) {
          faqs = apiFaqs;

          resetPage();

          render();
        }

      } catch (error) {

        /*
          Liquid 已经成功显示，
          后台 API 出错不影响用户。
        */
        if (liquidFaqs.length) {
          console.warn(
            "FAQ API failed. Using Liquid data.",
            error
          );

          return;
        }

        showError(error);
      }
    }

    load();
  }

  /* =========================================================
     BOOT
  ========================================================= */

  function boot(scope = document) {
    scope
      .querySelectorAll(
        "[data-enecess-faq]"
      )
      .forEach(initFaq);

    scheduleHeaderUpdate();
  }

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        boot();
      }
    );
  } else {
    boot();
  }

  /* =========================================================
     HEADER EVENTS
  ========================================================= */

  window.addEventListener(
    "load",
    () => {
      scheduleHeaderUpdate();

      setTimeout(
        scheduleHeaderUpdate,
        250
      );

      setTimeout(
        scheduleHeaderUpdate,
        800
      );
    }
  );

  window.addEventListener(
    "resize",
    scheduleHeaderUpdate
  );

  window.addEventListener(
    "orientationchange",
    () => {
      setTimeout(
        scheduleHeaderUpdate,
        100
      );
    }
  );

  /*
    原代码滚动时非常频繁检测 Header。
    现在限制为250ms一次。
  */
  let lastScrollCheck = 0;

  window.addEventListener(
    "scroll",
    () => {
      const now = Date.now();

      if (
        now - lastScrollCheck <
        250
      ) {
        return;
      }

      lastScrollCheck = now;

      scheduleHeaderUpdate();
    },
    {
      passive: true
    }
  );

  /* =========================================================
     SHOPIFY THEME EDITOR
  ========================================================= */

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      boot(event.target);
    }
  );
  /* =========================================================
     FAQ IMAGE LIGHTBOX
     点击 FAQ 图片放大
  ========================================================= */

  function initFaqImageLightbox() {

    /* 防止重复创建 */
    if (document.querySelector(".faq-image-lightbox")) {
      return;
    }

    /* 创建大图容器 */
    const lightbox = document.createElement("div");

    lightbox.className = "faq-image-lightbox";

    lightbox.setAttribute(
      "aria-hidden",
      "true"
    );

    lightbox.innerHTML = `
      <button
        type="button"
        class="faq-image-lightbox-close"
        aria-label="Close image"
      >
        ×
      </button>

      <img
        src=""
        alt=""
      >
    `;

    document.body.appendChild(lightbox);


    const lightboxImage =
      lightbox.querySelector("img");

    const closeButton =
      lightbox.querySelector(
        ".faq-image-lightbox-close"
      );


    /* 打开大图 */
    function openLightbox(image) {

      lightboxImage.src =
        image.currentSrc ||
        image.src;

      lightboxImage.alt =
        image.alt || "";

      lightbox.classList.add("active");

      lightbox.setAttribute(
        "aria-hidden",
        "false"
      );

      document.body.classList.add(
        "faq-lightbox-open"
      );
    }


    /* 关闭大图 */
    function closeLightbox() {

      lightbox.classList.remove("active");

      lightbox.setAttribute(
        "aria-hidden",
        "true"
      );

      document.body.classList.remove(
        "faq-lightbox-open"
      );

      lightboxImage.src = "";
    }


    /* 
       使用事件代理
       所以以后新增加的 FAQ 图片
       也会自动支持点击放大
    */
    document.addEventListener(
      "click",
      function (event) {

        const image =
          event.target.closest(
            ".enecess-faq .faq-images img"
          );

        if (image) {

          event.preventDefault();

          openLightbox(image);

          return;
        }


        /* 点击黑色背景关闭 */
        if (
          event.target === lightbox ||
          event.target === closeButton
        ) {
          closeLightbox();
        }

      }
    );


    /* ESC 关闭 */
    document.addEventListener(
      "keydown",
      function (event) {

        if (
          event.key === "Escape" &&
          lightbox.classList.contains("active")
        ) {
          closeLightbox();
        }

      }
    );

  }


  /* 初始化图片放大功能 */
  if (document.readyState === "loading") {

    document.addEventListener(
      "DOMContentLoaded",
      initFaqImageLightbox
    );

  } else {

    initFaqImageLightbox();

  }
})();